import React, { useState, useEffect, useCallback, useRef } from "react";

import {
  listCredentials,
  createCredential,
  updateCredential,
  deleteCredential,
  listOAuthProviders,
  beginOAuthConnect,
  testCredentialConnection,
} from "../../../../api";
import { useNotify } from "../../../../ToastContext";
import { usePermission } from "../../../../UserContext";
import { headersToJson } from "../../../../utils/timeUtils";
import Select from "../../../../components/Select";
import PasswordInput from "../../../../components/PasswordInput";
import plusIcon from "../../../../assets/plus.svg";
import xmarkIcon from "../../../../assets/xmark.svg";
import moreIcon from "../../../../assets/more-horiz.svg";
import { ProviderBadge } from "./providerIcons";

const POLL_INTERVAL = 10000;

// Slack scopes are comma-delimited; everyone else is space-delimited. The
// parser handles both formats so we don't have to thread provider context
// through every render path.
const parseScopes = (c) => {
  const raw = c?.oauth_scopes || "";
  if (!raw) return [];
  const sep = raw.includes(",") ? "," : " ";
  return raw.split(sep).map((s) => s.trim()).filter(Boolean);
};

// Provider scope identifiers can be very long URLs (Google in particular).
// Trim them to the last path segment for the chip while keeping the full
// scope on hover via title.
const shortenScope = (s) => {
  if (!s) return s;
  if (s.startsWith("https://")) {
    const idx = s.lastIndexOf("/");
    return idx >= 0 ? s.slice(idx + 1) : s;
  }
  return s;
};

// CredentialStatusBadge surfaces three states:
//   - Needs reconnect (refresh failed / scope revoked) — red, dominant
//   - Recently tested OK — green, transient
//   - Token expired (oauth_expires_at past + no refresh token) — yellow
// Manual credentials (bearer/basic) get no badge — they don't have a
// background failure mode the user could miss.
function CredentialStatusBadge({ credential, testResult }) {
  if (credential.auth_mode !== "oauth2") return null;
  if (credential.oauth_needs_reconnect) {
    return (
      <span
        className="gwCredentialStatus gwCredentialStatus--bad"
        title={credential.oauth_last_error || "Needs reconnect"}
      >
        Needs reconnect
      </span>
    );
  }
  if (testResult?.ok) {
    return <span className="gwCredentialStatus gwCredentialStatus--ok">Connected</span>;
  }
  if (credential.oauth_expires_at) {
    const expiresAt = new Date(credential.oauth_expires_at).getTime();
    if (expiresAt < Date.now()) {
      return <span className="gwCredentialStatus gwCredentialStatus--warn">Expired</span>;
    }
  }
  return null;
}

// auth_mode dropdown options — OAuth2 is intentionally absent here because
// OAuth credentials are minted by the provider redirect flow, not by POSTing
// to /api/credentials. Use the "Connect a service" button instead.
const AUTH_OPTIONS = [
  { value: "none", label: "None" },
  { value: "bearer", label: "Bearer Token" },
  { value: "basic", label: "Basic Auth" },
];

// ── Connect-Service Modal ───────────────────────────────────────────
//
// Lists configured OAuth providers. Clicking one POSTs to
// /api/credentials/oauth/{provider}/start which returns an authorize URL;
// we redirect the browser there. The provider eventually redirects back to
// our callback, which persists the credential and bounces back to this
// page (via final_return).
// `replaces` (when set) is the existing OAuth credential being reconnected.
// In that mode the modal pre-filters to the matching provider and passes
// replaces_credential_id through so the backend rotates tokens in place
// rather than minting a new credential row.
const ConnectServiceModal = ({ onClose, replaces }) => {
  const notify = useNotify();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    listOAuthProviders()
      .then((all) => {
        if (!replaces) {
          setProviders(all || []);
          return;
        }
        // Reconnect path — only show the matching provider so the user
        // can't accidentally swap a Slack credential for a Google one.
        const match = (all || []).filter((p) => p.id === replaces.provider);
        setProviders(match);
      })
      .catch((err) => notify({ type: "error", title: "Failed", message: String(err.message || err) }))
      .finally(() => setLoading(false));
  }, [notify, replaces]);

  const handleConnect = async (provider) => {
    setBusy(provider.id);
    try {
      const finalReturn = window.location.origin + window.location.pathname + window.location.search;
      const payload = { final_return: finalReturn };
      if (replaces?.id) payload.replaces_credential_id = replaces.id;
      const resp = await beginOAuthConnect(provider.id, payload);
      if (resp?.authorize_url) {
        window.location.href = resp.authorize_url;
        return;
      }
      throw new Error("server did not return authorize_url");
    } catch (err) {
      notify({ type: "error", title: "Couldn't start OAuth", message: String(err.message || err) });
      setBusy(null);
    }
  };

  const noneConfigured = !loading && providers.length > 0 && !providers.some((p) => p.configured);

  return (
    <div className="gwOverlay" onClick={onClose}>
      <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 520 }}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">
              {replaces ? `Reconnect ${replaces.name}` : "Connect a service"}
            </div>
            <div className="gwModalSubtitle">
              {replaces
                ? "Re-authorize the existing credential. Flow nodes referencing it stay pointed at the same id."
                : "Authorize Control Seat to act on your behalf in Slack, Gmail, Notion, etc."}
            </div>
          </div>
          <button className="gwModalClose" type="button" onClick={onClose}>
            <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
          </button>
        </div>
        <div className="gwModalBody">
          {loading && (
            <div className="gwConnectProviderEmpty">Loading providers...</div>
          )}
          {!loading && providers.length === 0 && (
            <div className="gwConnectProviderEmpty">No OAuth providers registered on the server.</div>
          )}
          {noneConfigured && (
            <div className="gwConnectProviderBanner">
              No providers are configured yet. An admin needs to set the client_id / client_secret
              for at least one provider on the{" "}
              <strong>Connectors → OAuth Apps</strong> page first.
            </div>
          )}
          <div className="gwConnectProviderList">
            {providers.map((p) => {
              const status = p.configured
                ? busy === p.id
                  ? "Redirecting…"
                  : "Click to authorize"
                : "Not configured";
              return (
                <button
                  key={p.id}
                  type="button"
                  className="gwConnectProviderRow"
                  disabled={!p.configured || busy === p.id}
                  onClick={() => p.configured && handleConnect(p)}
                >
                  <ProviderBadge providerId={p.id} />
                  <span className="gwConnectProviderInfo">
                    <span className="gwConnectProviderName">{p.label}</span>
                    <span className="gwConnectProviderStatus">{status}</span>
                  </span>
                  {p.configured && <span className="gwConnectProviderArrow" aria-hidden="true">→</span>}
                </button>
              );
            })}
          </div>
        </div>
        <div className="gwModalFooter">
          <button className="gwGhostBtn" type="button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ── Credential Modal ───────────────────────────────────────────────────

const CredentialModal = ({ editing, prefilled, onClose, onSaved }) => {
  const isEdit = !!editing;
  const notify = useNotify();
  const seed = editing || prefilled || null;

  const [form, setForm] = useState({
    name: seed?.name || "",
    auth_mode: seed?.auth_mode || "bearer",
    auth_username: seed?.auth_username || seed?.username || "",
    auth_secret: "",
    headers: seed?.headers ? headersToJson(seed.headers) : headersToJson(editing?.headers),
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (field) => (e) => {
    const value = typeof e === "string" ? e : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let headers;
    if (form.headers.trim()) {
      try {
        const parsed = JSON.parse(form.headers.trim());
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
          notify({ type: "error", title: "Invalid Headers", message: "Headers must be a JSON object." });
          return;
        }
        headers = parsed;
      } catch {
        notify({ type: "error", title: "Invalid Headers", message: "Custom headers must be valid JSON." });
        return;
      }
    }

    setSubmitting(true);
    try {
      if (isEdit) {
        const patch = {};
        if (form.name !== editing.name) patch.name = form.name;
        if (form.auth_mode !== (editing.auth_mode || "none") || form.auth_secret) {
          patch.auth_mode = form.auth_mode;
          if (form.auth_mode === "basic") patch.auth_username = form.auth_username;
          if (form.auth_mode !== "none") patch.auth_secret = form.auth_secret;
        }
        if (headers !== undefined) patch.headers = headers;
        else if (!form.headers.trim() && editing.headers && Object.keys(editing.headers).length > 0) patch.headers = {};
        await updateCredential(editing.id, patch);
      } else {
        const payload = { name: form.name, auth_mode: form.auth_mode };
        if (form.auth_mode === "basic") payload.auth_username = form.auth_username;
        if (form.auth_mode !== "none") payload.auth_secret = form.auth_secret;
        if (headers) payload.headers = headers;
        await createCredential(payload);
      }
      notify({
        type: "success",
        title: isEdit ? "Updated" : "Created",
        message: `"${form.name}" ${isEdit ? "updated" : "created"}.`,
      });
      onSaved();
      onClose();
    } catch (err) {
      notify({ type: "error", title: "Failed", message: String(err.message || err) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="gwOverlay" onClick={onClose}>
      <form className="gwModal" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit} style={{ width: 480 }}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">{isEdit ? `Edit: ${editing.name}` : "New Credential"}</div>
            <div className="gwModalSubtitle">
              {isEdit
                ? "Update the credential."
                : "Saved auth (bearer / basic) for outbound HTTP calls in flow action.http nodes."}
            </div>
          </div>
          <button className="gwModalClose" type="button" onClick={onClose}>
            <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
          </button>
        </div>

        <div className="gwModalBody">
          <div className="gwField">
            <label className="gwLabel">Name</label>
            <input
              className="gwInput"
              placeholder="e.g. Weather API"
              value={form.name}
              onChange={set("name")}
              autoFocus
              required
            />
          </div>

          <div className="gwField">
            <label className="gwLabel">Authentication</label>
            <Select value={form.auth_mode} onChange={set("auth_mode")} options={AUTH_OPTIONS} />
          </div>

          {form.auth_mode === "basic" && (
            <div className="gwFieldRow">
              <div className="gwField">
                <label className="gwLabel">Username</label>
                <input
                  className="gwInput"
                  placeholder="API username"
                  value={form.auth_username}
                  onChange={set("auth_username")}
                />
              </div>
              <div className="gwField">
                <label className="gwLabel">Password</label>
                <PasswordInput
                  placeholder={isEdit ? "Leave blank to keep current" : "Enter password"}
                  value={form.auth_secret}
                  onChange={set("auth_secret")}
                />
              </div>
            </div>
          )}

          {form.auth_mode === "bearer" && (
            <div className="gwField">
              <label className="gwLabel">Bearer Token</label>
              <PasswordInput
                placeholder={isEdit ? "Leave blank to keep current" : "Enter token"}
                value={form.auth_secret}
                onChange={set("auth_secret")}
              />
            </div>
          )}

          <div className="gwField">
            <label className="gwLabel">Custom Headers (JSON)</label>
            <textarea
              className="gwTextarea"
              rows={3}
              placeholder={'{\n    "X-Api-Version": "2024-01"\n}'}
              value={form.headers}
              onChange={set("headers")}
            />
            <div className="gwHint">
              Optional key-value pairs sent with every outbound request that uses this credential.
            </div>
          </div>
        </div>

        <div className="gwModalFooter">
          <button className="gwGhostBtn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="gwPrimaryBtn" type="submit" disabled={submitting}>
            {submitting ? "Saving..." : isEdit ? "Save" : "Create Credential"}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Main Page ───────────────────────────────────────────────────────

const ApiCredentialsPage = () => {
  const canConfigure = usePermission("configure");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [reconnecting, setReconnecting] = useState(null); // existing credential to replace
  const [editing, setEditing] = useState(null);
  const [proposed, setProposed] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [testing, setTesting] = useState(null); // credential id currently being tested
  const [testResults, setTestResults] = useState({}); // id → { ok, label, error, at }
  // After an OAuth callback we land back here with ?credential_id=... so the
  // user sees the new credential land. We capture it once on mount, then
  // strip the query param so a refresh doesn't keep re-flashing the row.
  const [highlightId, setHighlightId] = useState(() => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const id = params.get("credential_id");
    if (!id) return null;
    params.delete("credential_id");
    const newSearch = params.toString();
    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "") + window.location.hash;
    window.history.replaceState({}, "", newUrl);
    return id;
  });
  const highlightRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const notify = useNotify();

  const refresh = useCallback(async () => {
    try {
      setItems((await listCredentials()) || []);
    } catch (err) {
      notify({ type: "error", title: "Error", message: String(err.message || err) });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [refresh]);

  // Refresh when the AI mutates a credential.
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.resource === "credentials") refresh();
    };
    window.addEventListener("gw-ai-resource-changed", handler);
    return () => window.removeEventListener("gw-ai-resource-changed", handler);
  }, [refresh]);

  // AI propose-credential: open the New Credential modal pre-filled.
  useEffect(() => {
    const handler = (e) => {
      const payload = e.detail?.payload;
      if (!payload || typeof payload !== "object") return;
      setProposed(payload);
      setEditing(null);
      setShowCreate(true);
    };
    window.addEventListener("gw-ai-propose-credential-page", handler);
    return () => window.removeEventListener("gw-ai-propose-credential-page", handler);
  }, []);

  // Once the highlighted row exists in the DOM, scroll it into view + clear
  // the highlight after the flash animation runs its course.
  useEffect(() => {
    if (!highlightId) return undefined;
    if (!highlightRef.current) return undefined;
    highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(() => setHighlightId(null), 2500);
    return () => clearTimeout(t);
  }, [highlightId, items]);

  const openMenu = (e, id) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setMenuStyle({
      position: "fixed",
      top: `${Math.round(r.bottom + 4)}px`,
      right: `${Math.max(12, Math.round(window.innerWidth - r.right))}px`,
    });
    setMenuOpen(menuOpen === id ? null : id);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      const menu = document.querySelector(".gwProjectMenu");
      if (menu?.contains(e.target)) return;
      setMenuOpen(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const handleTest = async (c) => {
    setTesting(c.id);
    try {
      const result = await testCredentialConnection(c.id);
      setTestResults((r) => ({
        ...r,
        [c.id]: { ok: !!result?.ok, label: result?.account_label, error: result?.error, at: Date.now() },
      }));
      if (result?.ok) {
        notify({ type: "success", title: "Connected", message: result.account_label || "OK" });
      } else {
        notify({ type: "error", title: "Test failed", message: result?.error || "unknown" });
      }
      // Refresh list — needs_reconnect / last_error were just updated server-side.
      refresh();
    } catch (err) {
      notify({ type: "error", title: "Test failed", message: String(err.message || err) });
    } finally {
      setTesting(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteCredential(deleteTarget.id);
      notify({ type: "success", title: "Removed", message: `"${deleteTarget.name}" deleted.` });
      setDeleteTarget(null);
      refresh();
    } catch (err) {
      notify({ type: "error", title: "Error", message: String(err.message || err) });
    } finally {
      setDeleting(false);
    }
  };

  const hasItems = items.length > 0;

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">Credentials</div>
            <div className="gwPageSubtitle">
              {hasItems
                ? `${items.length} credential${items.length !== 1 ? "s" : ""} configured`
                : "Saved auth (bearer / basic) for flow action.http nodes — no URL or polling, just credentials."}
            </div>
          </div>
          <div style={{ display: "inline-flex", gap: 8 }}>
            <button
              className="gwGhostBtn"
              type="button"
              onClick={() => setShowConnect(true)}
              disabled={!canConfigure}
              title={canConfigure ? undefined : "Requires configure permission"}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <span>Connect a service</span>
            </button>
            <button
              className="gwPrimaryBtn"
              type="button"
              onClick={() => setShowCreate(true)}
              disabled={!canConfigure}
              title={canConfigure ? undefined : "Requires configure permission"}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
              <span>Add Credential</span>
            </button>
          </div>
        </div>

        {loading && !hasItems && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--gw-muted)", fontSize: 13 }}>
            Loading credentials...
          </div>
        )}

        {!loading && !hasItems && (
          <div className="gwEmptyState">
            <div className="gwEmptyTitle">No credentials yet</div>
            <div className="gwEmptyBody">
              Connect a service (Slack, Gmail, Notion, …) to grant Control Seat access via OAuth, or
              add a manual credential (bearer token / basic auth) that flow action.http nodes can
              reference.
            </div>
            <div className="gwEmptyActions" style={{ display: "inline-flex", gap: 8 }}>
              <button
                className="gwGhostBtn"
                type="button"
                onClick={() => setShowConnect(true)}
                disabled={!canConfigure}
                title={canConfigure ? undefined : "Requires configure permission"}
              >
                Connect a service
              </button>
              <button
                className="gwPrimaryBtn"
                type="button"
                onClick={() => setShowCreate(true)}
                disabled={!canConfigure}
                title={canConfigure ? undefined : "Requires configure permission"}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
                <span>Add Credential</span>
              </button>
            </div>
          </div>
        )}

        {hasItems && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Credentials</div>
            <div className="gwCardSectionBody" style={{ padding: 0 }}>
              {items.map((c) => (
                <div key={c.id} data-gw-resource="credentials" data-gw-id={c.id} data-gw-name={c.name} ref={highlightId === c.id ? highlightRef : null}>
                  <div className={`umUserRow${highlightId === c.id ? " gwCredentialHighlight" : ""}`}>
                    {c.auth_mode === "oauth2" ? (
                      <ProviderBadge providerId={c.provider} size={36} />
                    ) : (
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 10,
                          flexShrink: 0,
                          background: "var(--gw-surface-2)",
                          border: "1px solid var(--gw-border)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 11,
                          fontWeight: 700,
                          color: "var(--gw-text-secondary)",
                        }}
                      >
                        {c.auth_mode === "basic" ? "BA" : c.auth_mode === "bearer" ? "BR" : "—"}
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span className="umUserName">{c.name}</span>
                        <CredentialStatusBadge credential={c} testResult={testResults[c.id]} />
                      </div>
                      <div className="umUserMeta">
                        {c.auth_mode === "oauth2"
                          ? `${c.provider || "oauth"}${c.oauth_account_label ? " \u00b7 " + c.oauth_account_label : ""}`
                          : c.auth_mode}
                        {c.auth_mode !== "oauth2" && !c.has_auth_secret ? " \u00b7 no secret set" : ""}
                      </div>
                      {c.auth_mode === "oauth2" && c.oauth_scopes && (
                        <div className="gwCredentialScopeChips">
                          {parseScopes(c).map((s) => (
                            <span key={s} className="gwCredentialScopeChip" title={s}>
                              {shortenScope(s)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="gwIconButton"
                      style={{ flexShrink: 0 }}
                      onClick={(e) => openMenu(e, c.id)}
                    >
                      <img src={moreIcon} alt="More" className="gwIcon" style={{ width: 16, height: 16 }} />
                    </button>
                    {menuOpen === c.id && (
                      <div className="gwProjectMenu" style={menuStyle} onClick={(e) => e.stopPropagation()}>
                        {c.auth_mode !== "oauth2" && (
                          <>
                            <button
                              className="gwProjectMenuItem"
                              type="button"
                              disabled={!canConfigure}
                              title={canConfigure ? undefined : "Requires configure permission"}
                              onClick={() => {
                                setMenuOpen(null);
                                setEditing(c);
                              }}
                            >
                              Edit
                            </button>
                            <div className="gwProfileDivider" />
                          </>
                        )}
                        {c.auth_mode === "oauth2" && (
                          <>
                            <button
                              className="gwProjectMenuItem"
                              type="button"
                              disabled={testing === c.id}
                              onClick={() => {
                                setMenuOpen(null);
                                handleTest(c);
                              }}
                            >
                              {testing === c.id ? "Testing..." : "Test connection"}
                            </button>
                            <button
                              className="gwProjectMenuItem"
                              type="button"
                              disabled={!canConfigure}
                              title={canConfigure ? undefined : "Requires configure permission"}
                              onClick={() => {
                                setMenuOpen(null);
                                setReconnecting(c);
                              }}
                            >
                              Reconnect
                            </button>
                            <div className="gwProfileDivider" />
                          </>
                        )}
                        <button
                          className="gwProjectMenuItem gwProjectMenuDanger"
                          type="button"
                          disabled={!canConfigure}
                          title={canConfigure ? undefined : "Requires configure permission"}
                          onClick={() => {
                            setMenuOpen(null);
                            setDeleteTarget(c);
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {showCreate && (
        <CredentialModal
          prefilled={proposed}
          onClose={() => { setShowCreate(false); setProposed(null); }}
          onSaved={refresh}
        />
      )}
      {editing && <CredentialModal editing={editing} onClose={() => setEditing(null)} onSaved={refresh} />}
      {showConnect && <ConnectServiceModal onClose={() => setShowConnect(false)} />}
      {reconnecting && (
        <ConnectServiceModal
          replaces={reconnecting}
          onClose={() => setReconnecting(null)}
        />
      )}

      {deleteTarget && (
        <div className="gwOverlay" onClick={() => setDeleteTarget(null)}>
          <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 400 }}>
            <div className="gwModalHeader">
              <div>
                <div className="gwModalTitle">Remove Credential</div>
                <div className="gwModalSubtitle">This action cannot be undone.</div>
              </div>
              <button className="gwModalClose" type="button" onClick={() => setDeleteTarget(null)}>
                <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
              </button>
            </div>
            <div className="gwModalBody">
              <p style={{ fontSize: 13, color: "var(--gw-text-secondary)", margin: 0 }}>
                Are you sure you want to remove <strong>{deleteTarget.name}</strong>? Flow action.http nodes
                referencing this credential will start failing on auth.
              </p>
            </div>
            <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
              <button className="gwGhostBtn" type="button" onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
              <button className="gwDangerBtn" type="button" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Removing..." : "Remove Credential"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { ApiCredentialsPage };
